import type {
  CarrierConfigEntry,
  CommissionSightClient,
  InferredConfig,
} from '@commissionsight/sdk';
import {
  type CarrierConfigDraft,
  buildCarrierConfig,
  draftFromConfig,
  emptyCarrierConfigDraft,
  mappingRowDraft,
} from '@commissionsight/shared';
import { useCallback, useEffect, useState } from 'react';
import { CarrierConfigEditor } from '../components/CarrierConfigEditor.js';
import { CarrierRequestsPanel } from '../components/CarrierRequestsPanel.js';
import {
  ErrorNote,
  IconButton,
  type Notify,
  Pager,
  Panel,
  btn,
  btnGhost,
  input,
  useRun,
} from '../components/ui.js';
import {
  type AiBuildResult,
  adminAiBuildMapping,
  createCarrierGroup,
  listCarrierGroups,
  setCarrierGroup,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { useClientPaged } from '../lib/usePaged.js';

/** A fresh draft for hand-authoring a new config, pre-seeded with the common pair. */
function seedDraft(): CarrierConfigDraft {
  const d = emptyCarrierConfigDraft();
  d.memberKey = 'Member ID';
  d.mappings = [
    mappingRowDraft({ source: 'Member ID', target: 'member_external_id' }),
    mappingRowDraft({ source: 'Commission', target: 'commission_amount' }),
  ];
  return d;
}

export function Carriers({ client, notify }: { client: CommissionSightClient; notify: Notify }) {
  const run = useRun(notify);
  const { data, error, reload } = useAsyncData(
    useCallback(() => client.listCarriers().then((r) => r.data), [client]),
  );
  const carriers = data ?? [];
  const paged = useClientPaged(carriers, 15);

  // Carrier brands (groups) for content-routed upload.
  const groupsState = useAsyncData(useCallback(() => listCarrierGroups(), []));
  const groups = groupsState.data ?? [];
  const [gName, setGName] = useState('');
  const [gSlug, setGSlug] = useState('');

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [draft, setDraft] = useState<CarrierConfigDraft>(seedDraft);
  const [srcHeaders, setSrcHeaders] = useState<string[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [editName, setEditName] = useState<Record<string, string>>({});
  const [cfgCarrierId, setCfgCarrierId] = useState('');
  const [cfgList, setCfgList] = useState<CarrierConfigEntry[]>([]);
  const [editCfgId, setEditCfgId] = useState('');
  const [editDraft, setEditDraft] = useState<CarrierConfigDraft | null>(null);
  // Adapter so CarrierConfigEditor (expects a non-null setter) can drive the
  // nullable edit draft — a no-op while nothing is being edited.
  const setEdit: typeof setDraft = (action) =>
    setEditDraft((cur) => (cur ? (typeof action === 'function' ? action(cur) : action) : cur));
  // Onboarding wizard: drop a sample file → inferred draft config.
  const [wizCarrier, setWizCarrier] = useState('');
  const [wizFile, setWizFile] = useState<File | null>(null);
  const [wizBusy, setWizBusy] = useState(false);
  const [wizInfo, setWizInfo] = useState<InferredConfig | null>(null);
  // AI training: build/reuse a mapping for a statement shape via the signature library.
  const [aiFile, setAiFile] = useState<File | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState<AiBuildResult | null>(null);

  const aiBuild = async () => {
    if (!wizCarrier || !aiFile) {
      setStatus('Pick a carrier and a sample file first.');
      return;
    }
    setAiBusy(true);
    setStatus(null);
    setAiResult(null);
    try {
      const res = await adminAiBuildMapping(wizCarrier, aiFile);
      setAiResult(res);
      setStatus(
        res.built
          ? `AI built a mapping (${res.coverage?.mapped ?? 0}/${res.coverage?.total ?? 0} rows) — saved as the carrier’s global config and reused for every future upload of this format.`
          : res.action.startsWith('reuse')
            ? 'We already have a mapping for this file format — reused it, nothing to build.'
            : 'Done.',
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not build a mapping');
    } finally {
      setAiBusy(false);
    }
  };

  // Seed the editable carrier names whenever the carrier list (re)loads.
  useEffect(() => {
    if (data) setEditName(Object.fromEntries(data.map((cc) => [cc.id, cc.name])));
  }, [data]);

  const analyzeSample = async () => {
    if (!wizCarrier || !wizFile) {
      setStatus('Pick a carrier and a sample file first.');
      return;
    }
    setWizBusy(true);
    setStatus(null);
    try {
      const res = await client.admin.inferConfig(wizCarrier, wizFile);
      setWizInfo(res);
      setDraft(draftFromConfig(res.config));
      setSrcHeaders([...res.mapped.map((m) => m.header), ...res.unmapped]);
      setCarrierId(wizCarrier);
      setStatus(
        `Draft ready (${Math.round(res.confidence * 100)}% confidence) — review below and save.`,
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not analyze sample');
    } finally {
      setWizBusy(false);
    }
  };

  const loadConfigs = useCallback(
    async (cid: string) => {
      setEditCfgId('');
      setEditDraft(null);
      if (!cid) {
        setCfgList([]);
        return;
      }
      try {
        const r = await client.admin.listCarrierConfigs(cid);
        setCfgList(r.data);
      } catch (e) {
        setCfgList([]);
        notify(e instanceof Error ? e.message : 'Failed to load configs', 'error');
      }
    },
    [client, notify],
  );

  return (
    <>
      <CarrierRequestsPanel
        carriers={carriers.map((c) => ({ id: c.id, name: c.name }))}
        notify={notify}
        onDecided={() => void reload()}
      />
      <Panel title="Carriers & global configs">
        <div className="mb-4 flex gap-2">
          <input
            className={input}
            placeholder="Carrier name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className={input}
            placeholder="slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
          <button
            type="button"
            className={btn}
            onClick={() =>
              run(async () => {
                const res = await client.admin.createCarrier(name, slug);
                setCarrierId(res.id);
                setName('');
                setSlug('');
                setStatus(`Created carrier ${res.id}`);
                reload();
              })
            }
          >
            Create carrier
          </button>
        </div>

        {error && (
          <div className="mb-4">
            <ErrorNote message={error} onRetry={reload} />
          </div>
        )}

        <div className="mb-5 rounded-lg border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th>Slug</th>
                <th>Brand</th>
                <th className="text-right" />
              </tr>
            </thead>
            <tbody>
              {paged.pageItems.map((cc) => (
                <tr key={cc.id} className="border-t border-neutral-100">
                  <td className="px-3 py-2">
                    <input
                      className={input}
                      value={editName[cc.id] ?? ''}
                      onChange={(e) => setEditName((m) => ({ ...m, [cc.id]: e.target.value }))}
                    />
                  </td>
                  <td className="font-mono text-xs text-neutral-500">{cc.slug}</td>
                  <td className="px-3">
                    <select
                      className={input}
                      value={cc.groupId ?? ''}
                      onChange={(e) =>
                        run(async () => {
                          await setCarrierGroup(cc.id, e.target.value || null);
                          reload();
                          groupsState.reload();
                        })
                      }
                    >
                      <option value="">—</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 text-right">
                    <IconButton
                      icon="save"
                      label="Save carrier name"
                      tone="emerald"
                      disabled={!editName[cc.id]?.trim() || editName[cc.id] === cc.name}
                      onClick={() =>
                        run(async () => {
                          const newName = (editName[cc.id] ?? '').trim();
                          if (!newName) return;
                          await client.admin.renameCarrier(cc.id, newName);
                          setStatus(`Renamed to “${newName}”`);
                          reload();
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {carriers.length === 0 && !error && (
            <p className="px-3 py-3 text-sm text-neutral-500">No carriers yet.</p>
          )}
          <div className="px-3">
            <Pager
              offset={paged.offset}
              count={paged.count}
              total={paged.total}
              hasMore={paged.hasMore}
              onPrev={paged.prev}
              onNext={paged.next}
            />
          </div>
        </div>

        <div className="mb-5 rounded-xl border border-neutral-200 p-3">
          <h3 className="mb-1 font-semibold">Carrier brands</h3>
          <p className="mb-2 text-xs text-neutral-500">
            Group per-product carrier records under a brand (e.g. “UHC” over its Commercial /
            Medicare Supplement / Medicare Advantage rows). Customers pick the brand at upload and
            the product is detected from the file. Assign carriers to a brand in the Brand column
            above.
          </p>
          <div className="mb-3 flex gap-2">
            <input
              className={input}
              placeholder="Brand name (e.g. UHC)"
              value={gName}
              onChange={(e) => setGName(e.target.value)}
            />
            <input
              className={input}
              placeholder="slug"
              value={gSlug}
              onChange={(e) => setGSlug(e.target.value)}
            />
            <button
              type="button"
              className={btn}
              disabled={!gName.trim() || !gSlug.trim()}
              onClick={() =>
                run(async () => {
                  await createCarrierGroup(gName.trim(), gSlug.trim());
                  setGName('');
                  setGSlug('');
                  setStatus(`Created brand “${gName.trim()}”`);
                  groupsState.reload();
                })
              }
            >
              Create brand
            </button>
          </div>
          {groups.length === 0 ? (
            <p className="text-sm text-neutral-500">No brands yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {groups.map((g) => (
                <li
                  key={g.id}
                  className="flex items-center justify-between rounded border border-neutral-100 px-3 py-1.5"
                >
                  <span>
                    <span className="font-medium">{g.name}</span>{' '}
                    <span className="font-mono text-xs text-neutral-400">{g.slug}</span>
                  </span>
                  <span className="text-xs text-neutral-500">
                    {g.members.length} carrier{g.members.length === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-50/50 p-3">
          <h3 className="mb-1 font-semibold">Onboard from a sample file</h3>
          <p className="mb-2 text-xs text-neutral-500">
            Drop a carrier statement and CommissionSight maps the columns and detects the
            formatting. Review the generated draft below, then save.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${input} max-w-[14rem]`}
              value={wizCarrier}
              onChange={(e) => setWizCarrier(e.target.value)}
            >
              <option value="">Select carrier…</option>
              {carriers.map((cc) => (
                <option key={cc.id} value={cc.id}>
                  {cc.name}
                </option>
              ))}
            </select>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setWizFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
            <button type="button" className={btn} disabled={wizBusy} onClick={analyzeSample}>
              {wizBusy ? 'Analyzing…' : 'Analyze sample'}
            </button>
          </div>
          {wizInfo && (
            <div className="mt-2 text-xs text-neutral-600">
              <span className="font-medium">
                {Math.round(wizInfo.confidence * 100)}% confidence
              </span>{' '}
              · {wizInfo.mapped.length} columns mapped
              {wizInfo.unmapped.length > 0 ? ` · ${wizInfo.unmapped.length} kept in raw` : ''}
              {wizInfo.preview
                ? ` · preview: ${wizInfo.preview.mapped} mapped, ${wizInfo.preview.failed} failed`
                : ''}
              {wizInfo.notes.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {wizInfo.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}
              <p className="mt-1 italic">Draft loaded into the editor below — review and save.</p>
            </div>
          )}
        </div>

        <div className="mb-4 rounded-xl border border-indigo-500/30 bg-indigo-50/50 p-3">
          <h3 className="mb-1 font-semibold">Train AI to recognize a statement</h3>
          <p className="mb-2 text-xs text-neutral-500">
            Upload a sample and AI maps its columns automatically — reusing a saved mapping if we’ve
            seen this file format before, or building a new one. The mapping is saved as the
            carrier’s global config and reused for every future upload of this format. Only masked
            column shapes are sent to the model — never member data.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${input} max-w-[14rem]`}
              value={wizCarrier}
              onChange={(e) => setWizCarrier(e.target.value)}
            >
              <option value="">Select carrier…</option>
              {carriers.map((cc) => (
                <option key={cc.id} value={cc.id}>
                  {cc.name}
                </option>
              ))}
            </select>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setAiFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
            <button type="button" className={btn} disabled={aiBusy} onClick={aiBuild}>
              {aiBusy ? 'Training…' : 'Train AI'}
            </button>
          </div>
          {aiResult && (
            <div className="mt-2 text-xs text-neutral-600">
              <span className="font-medium">
                {aiResult.built
                  ? 'Built new mapping'
                  : aiResult.action === 'reuse'
                    ? 'Reused verified mapping'
                    : 'Reused candidate mapping'}
              </span>
              {aiResult.coverage
                ? ` · ${aiResult.coverage.mapped}/${aiResult.coverage.total} rows mapped`
                : ''}
              {aiResult.configId ? ` · config ${aiResult.configId.slice(0, 8)}` : ''}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <input
            className={input}
            placeholder="Carrier ID (target for the saved config)"
            value={carrierId}
            onChange={(e) => setCarrierId(e.target.value)}
          />
          <CarrierConfigEditor draft={draft} setDraft={setDraft} sourceHeaders={srcHeaders} />
          <div className="flex gap-2">
            <button
              type="button"
              className={btn}
              disabled={!carrierId.trim()}
              onClick={() =>
                run(async () => {
                  await client.admin.createGlobalConfig(carrierId, buildCarrierConfig(draft));
                  setStatus('Global config saved');
                })
              }
            >
              Save global config
            </button>
            <button
              type="button"
              className={btnGhost}
              onClick={() => {
                setDraft(seedDraft());
                setSrcHeaders([]);
                setWizInfo(null);
              }}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="mt-6 border-t border-neutral-200 pt-5">
          <h3 className="mb-2 font-semibold">Edit existing config</h3>
          <div className="mb-3 flex gap-2">
            <select
              className={input}
              value={cfgCarrierId}
              onChange={(e) => {
                setCfgCarrierId(e.target.value);
                loadConfigs(e.target.value);
              }}
            >
              <option value="">Select a carrier…</option>
              {carriers.map((cc) => (
                <option key={cc.id} value={cc.id}>
                  {cc.name}
                </option>
              ))}
            </select>
          </div>
          {cfgCarrierId && cfgList.length === 0 && (
            <p className="text-sm text-neutral-500">No configs for this carrier yet.</p>
          )}
          <div className="space-y-1">
            {cfgList.map((cfg) => (
              <div
                key={cfg.id}
                className="flex items-center justify-between rounded border border-neutral-100 px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-medium">v{cfg.version}</span> · {cfg.fileType} ·{' '}
                  <span className={cfg.accountId ? 'text-sky-600' : 'text-neutral-500'}>
                    {cfg.accountId ? 'account override' : 'global'}
                  </span>
                  {!cfg.isActive && <span className="ml-1 text-rose-500">(inactive)</span>}
                </span>
                <IconButton
                  icon="edit"
                  label={`Edit config v${cfg.version}`}
                  tone="emerald"
                  onClick={() => {
                    setEditCfgId(cfg.id);
                    setEditDraft(draftFromConfig(cfg.config));
                  }}
                />
              </div>
            ))}
          </div>
          {editCfgId && editDraft && (
            <div className="mt-3 space-y-3 rounded-lg border border-neutral-200 p-3">
              <CarrierConfigEditor draft={editDraft} setDraft={setEdit} />
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btn}
                  onClick={() =>
                    run(async () => {
                      await client.admin.updateCarrierConfig(
                        cfgCarrierId,
                        editCfgId,
                        buildCarrierConfig(editDraft),
                      );
                      setStatus('Config updated');
                      loadConfigs(cfgCarrierId);
                    })
                  }
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className={btnGhost}
                  onClick={() => {
                    setEditCfgId('');
                    setEditDraft(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        {status && <p className="mt-3 text-sm text-neutral-500">{status}</p>}
      </Panel>
    </>
  );
}
